import { factoryCommandError } from './factory-status';
function formValue(form: FormData, key: string) {
  const value = form.get(key);
  return typeof value === 'string' ? value : '';
}
import { OperationsRecovery } from './operations-recovery';
import { useEffect, useState, type SyntheticEvent } from 'react';
import {
  factoryWorkspaceSchema,
  factoryPayloadSchema,
  operationsWorkspaceSchema,
  type FactoryWorkspace,
  type FactoryCommand,
} from '@sqi/shared-schemas';
import {
  request,
  loadHandoverAcknowledgements,
  type OemWorkspace,
  OemApiError,
} from '../../lib/oem-client';
import type { OemContext } from '../../lib/auth';
import { retainOperationsMutation, settleOperationsMutation } from '../../lib/operations-pending';
interface Field {
  name: string;
  label: string;
  options?: { id: string; label: string }[];
  value?: string;
  nullable?: boolean;
  type?: string;
}
export function FactoryWorkspacePage({
  context,
  workspace,
  userId,
}: {
  context: OemContext;
  workspace: OemWorkspace;
  userId: string;
}) {
  const [data, setData] = useState<FactoryWorkspace | null>(null),
    [caps, setCaps] = useState<string[]>([]),
    [message, setMessage] = useState('กำลังตรวจสิทธิ์โรงงาน'),
    [busy, setBusy] = useState(false),
    [orderHash, setOrderHash] = useState('');
  const refresh = async () => {
    setData(null);
    setCaps([]);
    const [raw, stock] = await Promise.all([
      request(`/factory/${context.organizationId}/${context.projectId}`),
      request(`/operations/${context.organizationId}/${context.projectId}`),
    ]);
    setData(factoryWorkspaceSchema.parse(raw));
    setCaps(operationsWorkspaceSchema.parse(stock).grants.map((g) => g.capability));
  };
  useEffect(() => {
    let active = true;
    void Promise.all([
      request(`/factory/${context.organizationId}/${context.projectId}`),
      request(`/operations/${context.organizationId}/${context.projectId}`),
    ])
      .then(([raw, stock]) => {
        if (active) {
          setData(factoryWorkspaceSchema.parse(raw));
          setCaps(operationsWorkspaceSchema.parse(stock).grants.map((g) => g.capability));
          setMessage('อ่านทะเบียนที่บันทึกแล้ว');
        }
      })
      .catch(() => {
        if (active) {
          setData(null);
          setCaps([]);
          setOrderHash('');
          setMessage('ต้องมีสิทธิ์ปฏิบัติการที่ยังใช้งานได้');
        }
      });
    if (workspace.order)
      void loadHandoverAcknowledgements(workspace.order.id)
        .then((x) => {
          if (active) setOrderHash(x.orderHash);
        })
        .catch(() => {
          if (active) setOrderHash('');
        });
    return () => {
      active = false;
    };
  }, [context.organizationId, context.projectId, workspace.order]);
  async function run(payload: FactoryCommand) {
    setBusy(true);
    const scope = {
      actorId: userId,
      organizationId: context.organizationId,
      projectId: context.projectId,
      action: payload.action,
    };
    try {
      if (!navigator.onLine) throw Error('online_required');
      const key = await retainOperationsMutation(sessionStorage, scope, payload);
      await request('/factory', {
        organizationId: context.organizationId,
        projectId: context.projectId,
        idempotencyKey: key,
        payload,
      });
      settleOperationsMutation(sessionStorage, scope, key);
      await refresh();
      setMessage('บันทึกแล้ว · โหลดทะเบียนยืนยันผลแล้ว');
    } catch (e) {
      setMessage(
        e instanceof OemApiError
          ? factoryCommandError(e.code)
          : 'ยังยืนยันผลไม่ได้ · ใช้ข้อมูลเดิมลองอีกครั้ง หรือกู้ผลที่บันทึกในหน้าคลัง',
      );
    } finally {
      setBusy(false);
    }
  }
  const auths =
    data?.authorizations.map((a) => ({
      id: a.id,
      label: `${a.facility} · ${a.plannedInputQuantity} ${a.unit} → ${a.plannedUnits} ชิ้น${a.revoked ? ' · เพิกถอน' : ''}`,
    })) ?? [];
  const batches =
    data?.batches.map((b) => ({
      id: b.id,
      label: `${b.label} · ${b.qaReleased ? 'QA ปล่อยแล้ว' : 'รอ QA'}`,
    })) ?? [];
  const packs =
    data?.batches.flatMap((b) =>
      b.packs.map((p) => ({
        id: p.id,
        label: `${b.label} · ${p.quantity} ชิ้น · ${p.qaReleased ? 'QA ปล่อยแล้ว' : 'รอ QA'}`,
      })),
    ) ?? [];
  const records = (kind: string) =>
    workspace.records
      .filter((r) => r.kind === kind)
      .map((r) => ({
        id: r.id,
        label: `${typeof r.content.label === 'string' ? r.content.label : kind} v${r.version}`,
      }));
  function form(action: FactoryCommand['action'], cap: string, title: string, fields: Field[]) {
    if (!caps.includes(cap)) return null;
    return (
      <form
        className="card form-grid"
        key={action}
        onSubmit={(e: SyntheticEvent<HTMLFormElement>) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          const payload: Record<string, unknown> = { action };
          for (const field of fields) {
            let v: unknown = formValue(f, field.name);
            if (field.nullable && v === '') v = null;
            if (field.type === 'datetime-local' && v) {
              const d = new Date(typeof v === 'string' ? v : '');
              if (!Number.isFinite(d.getTime())) {
                setMessage('วันเวลาไม่ถูกต้อง');
                return;
              }
              v = d.toISOString();
            }
            payload[field.name] = v;
          }
          payload.evidence = formValue(f, 'evidence');
          if (action === 'authorize') {
            payload.orderId = workspace.order?.id;
            payload.orderHash = orderHash;
          }
          if (action === 'create_parcel') {
            const pack = data?.batches.flatMap((b) => b.packs).find((p) => p.id === payload.packId),
              sample = workspace.records.find((r) => r.id === payload.sampleId);
            payload.sourceVersion = pack?.version ?? sample?.version;
            payload.sourceHash = pack?.hash ?? sample?.hash;
            payload.orderId = pack ? workspace.order?.id : null;
          }
          const p = factoryPayloadSchema.safeParse(payload);
          if (!p.success) {
            setMessage('ข้อมูลยังไม่ครบ หรือจำนวน/หน่วยไม่ถูกต้อง');
            return;
          }
          void run(p.data);
        }}
      >
        <h2>{title}</h2>
        {fields.map((f) => (
          <label key={f.name}>
            {f.label}
            {f.options ? (
              <select name={f.name} required={!f.nullable}>
                <option value="">เลือก</option>
                {f.options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                name={f.name}
                type={f.type ?? 'text'}
                defaultValue={f.value}
                required={!f.nullable}
              />
            )}
          </label>
        ))}
        <label>
          หลักฐาน/บันทึกผู้รับผิดชอบ
          <textarea name="evidence" required maxLength={4000} />
        </label>
        <button className="action" disabled={busy || (action === 'authorize' && !orderHash)}>
          บันทึก {title}
        </button>
      </form>
    );
  }
  const b: Field = { name: 'batchId', label: 'รุ่นผลิต', options: batches },
    a: Field = { name: 'authorizationId', label: 'คำอนุญาตโรงงาน', options: auths },
    pk: Field = { name: 'packId', label: 'รอบบรรจุ', options: packs };
  return (
    <>
      <h1>ผลิต บรรจุ และปล่อย QA</h1>
      <p>
        คำยืนยันลูกค้าและเชิงพาณิชย์ไม่แทนคำอนุญาตโรงงาน · บันทึกจริงโดยผู้รับผิดชอบและผู้ตรวจแยกกัน
        · ออนไลน์เท่านั้น
      </p>
      <p role="status">{message}</p>
      <button
        className="action secondary"
        onClick={() => void refresh().catch(() => setMessage('ยังอ่านทะเบียนไม่ได้'))}
      >
        โหลดทะเบียนอีกครั้ง
      </button>
      <p>
        ผล QA ที่แสดงคือประวัติที่บันทึกไว้ · ทุกคำสั่งตรวจฉบับ สิทธิ์ และความพร้อมปัจจุบันอีกครั้ง
      </p>
      <OperationsRecovery context={context} userId={userId} onRecovered={refresh} />
      {data?.batches.map((x) => (
        <section className="card" key={x.id}>
          <h2>{x.label}</h2>
          <p>
            {x.id} · {x.qaReleased ? 'QA ปล่อยแล้ว' : 'รอ QA'}
          </p>
          <p>
            ผลผลิต{' '}
            {x.output
              ? `${x.output.quantity} ${x.output.unit} · สูญเสีย ${x.output.lossQuantity}`
              : 'ยังไม่มีผลผลิต'}
          </p>
          <ul>
            {x.inputs.map((i) => (
              <li key={i.id}>
                วัตถุดิบ/ชิ้นส่วน {i.quantity} {i.unit} · {i.specificationId}
              </li>
            ))}
            {x.deviations.map((d) => (
              <li key={d.id}>
                {d.description} · {d.disposition} · {d.id}
              </li>
            ))}
            {x.packs.map((p) => (
              <li key={p.id}>
                {p.quantity} ชิ้น · {p.qaReleased ? 'QA ปล่อยแล้ว' : 'รอ QA'} · {p.id}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {form('authorize', 'factory_authorizer', 'อนุญาตโรงงานตามฉบับยืนยัน', [
        {
          name: 'skuId',
          label: 'สินค้า',
          options: workspace.skus.map((s) => ({ id: s.id, label: s.label })),
        },
        { name: 'facility', label: 'โรงงาน' },
        { name: 'plannedInputQuantity', label: 'ปริมาณวัตถุดิบฐาน' },
        {
          name: 'unit',
          label: 'หน่วยฐาน',
          options: [
            { id: 'g', label: 'กรัม' },
            { id: 'ml', label: 'มิลลิลิตร' },
          ],
        },
        { name: 'plannedUnits', label: 'จำนวนชิ้นที่วางแผน' },
        { name: 'validUntil', label: 'สิ้นสุดสิทธิ์คำอนุญาต', type: 'datetime-local' },
      ])}
      {form('revoke_authorization', 'factory_authorizer', 'เพิกถอนคำอนุญาต', [a])}
      {form('start_batch', 'production', 'เริ่มรุ่นผลิต', [
        a,
        { name: 'label', label: 'รหัสรุ่นผลิต' },
      ])}
      {form('consume', 'production', 'เบิกใช้ตามการจอง', [
        b,
        { name: 'reservationId', label: 'รหัสการจองจากคลัง' },
        { name: 'quantity', label: 'จำนวนใช้จริง' },
        {
          name: 'unit',
          label: 'หน่วยฐาน',
          options: ['g', 'ml', 'ea'].map((id) => ({ id, label: id })),
        },
      ])}
      {form('output', 'production', 'บันทึกผลผลิตและสูญเสีย', [
        b,
        { name: 'quantity', label: 'ผลผลิตจริง' },
        { name: 'lossQuantity', label: 'สูญเสียจริง', value: '0' },
        { name: 'unit', label: 'หน่วยฐาน', options: ['g', 'ml'].map((id) => ({ id, label: id })) },
      ])}
      {form('deviation', 'production', 'บันทึกความคลาดเคลื่อน', [
        b,
        { name: 'description', label: 'รายละเอียดที่ต้องตัดสิน' },
      ])}
      {form('decide_deviation', 'batch_qa', 'ตัดสินความคลาดเคลื่อน', [
        {
          name: 'deviationId',
          label: 'รายการ',
          options:
            data?.batches.flatMap((x) =>
              x.deviations.map((d) => ({ id: d.id, label: d.description })),
            ) ?? [],
        },
        {
          name: 'disposition',
          label: 'ผล',
          options: [
            { id: 'accepted', label: 'ยอมรับพร้อมหลักฐาน' },
            { id: 'rejected', label: 'ไม่ยอมรับ' },
          ],
        },
      ])}
      {form('qa_evidence', 'batch_qa', 'บันทึกผลทดสอบรุ่นผลิต', [
        b,
        { name: 'planId', label: 'แผนทดสอบฉบับตรึง', options: records('test_plan') },
        { name: 'reportFileId', label: 'รหัสรายงานที่ทบทวนแล้ว' },
        {
          name: 'outcome',
          label: 'ผลทดสอบ',
          options: [
            { id: 'passed', label: 'ผ่านตามผลจริง' },
            { id: 'failed', label: 'ไม่ผ่าน' },
          ],
        },
        { name: 'measuredValue', label: 'ค่าที่วัดและเกณฑ์' },
      ])}
      {form('release_batch', 'batch_qa', 'ปล่อยรุ่นผลิตโดย QA', [b])}
      {form('pack', 'production', 'บรรจุบางส่วน', [
        b,
        { name: 'quantity', label: 'จำนวนชิ้นจริง' },
      ])}
      {form('release_pack', 'batch_qa', 'ปล่อยรอบบรรจุโดย QA', [
        pk,
        { name: 'reportFileId', label: 'รหัสรายงานบรรจุที่ทบทวนแล้ว' },
      ])}
      {form('create_parcel', 'logistics', 'จัดพัสดุส่วนตัว', [
        { ...pk, nullable: true },
        {
          name: 'sampleId',
          label: 'หรือตัวอย่างฉบับจริง',
          options: records('sample'),
          nullable: true,
        },
        { name: 'quantity', label: 'จำนวนชิ้น' },
        { name: 'carrier', label: 'ผู้ขนส่ง (บันทึกเท่านั้น)' },
        { name: 'tracking', label: 'เลขติดตาม' },
        { name: 'recipientPrivate', label: 'ผู้รับ/ที่อยู่ส่วนตัว' },
      ])}
      <p>
        ตัวอย่างที่บันทึกเพียง g/ml ยังไม่ทราบจำนวนชิ้น: ต้องมีการเตรียมจริงฉบับที่ทบทวนแล้ว หน่วย
        ea จึงจัดพัสดุแบบแยกส่วนได้ งานจัดส่งและรับตัวอย่างเดิมยังใช้ได้
      </p>
      {data?.parcels.map((p) => (
        <section className="card" key={p.id}>
          <h2>
            {p.carrier} · {p.tracking}
          </h2>
          <p>
            {p.quantity} ชิ้น · ฉบับ {p.sourceVersion} · {p.id}
          </p>
          <p>ข้อมูลผู้รับส่วนตัว: {p.recipientPrivate}</p>
          <ul>
            {p.events.map((e, i) => (
              <li key={i}>
                {e.event} · {e.observedAt}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {form('parcel_event', 'logistics', 'บันทึกเหตุการณ์พัสดุ', [
        {
          name: 'parcelId',
          label: 'พัสดุ',
          options: data?.parcels.map((p) => ({ id: p.id, label: p.tracking })) ?? [],
        },
        {
          name: 'event',
          label: 'เหตุการณ์',
          options: [
            { id: 'dispatched', label: 'ส่งมอบขนส่ง' },
            { id: 'in_transit', label: 'อยู่ระหว่างขนส่ง' },
            { id: 'carrier_delivered', label: 'ขนส่งรายงานส่งถึง' },
            { id: 'delivery_failed', label: 'ส่งไม่สำเร็จ' },
            { id: 'returned', label: 'ส่งคืน' },
          ],
        },
        { name: 'observedAt', label: 'เวลาที่เกิดเหตุการณ์', type: 'datetime-local' },
      ])}
      {form('share_parcel', 'logistics', 'แชร์เฉพาะสรุปให้ลูกค้า', [
        {
          name: 'parcelId',
          label: 'พัสดุ',
          options: data?.parcels.map((p) => ({ id: p.id, label: p.tracking })) ?? [],
        },
      ])}
      <p>การบันทึกไม่ส่งข้อความ ไม่เรียกขนส่ง และไม่ยืนยันการรับ/ยอมรับตัวอย่างแทนลูกค้า</p>
    </>
  );
}
